/**
 * TemplateAttachmentsEditor - manage the static PDF packets attached to an
 * email template (WPS-159).
 *
 * Uploads go through the shared `/files/upload/` endpoint with
 * `use_case=EMAIL_TEMPLATE_ATTACHMENT` (PDF-only). Each row is one
 * `EmailTemplateAttachment`: an admin can rename it, replace the file (version
 * replacement - the row/label survive), or remove it. The parent owns the list
 * and persists it on the template's Save.
 */

import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ExternalLink,
  FileText,
  Loader2,
  RefreshCw,
  Trash2,
  Upload,
} from 'lucide-react'

import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { useUploadFile, type UploadedFile } from '@/admin/hooks/use-products'
import { toastApiError } from '@/shared/lib/api-error'
import type { AttachmentFileEnvelope, TemplateAttachment } from './types'

const ATTACHMENT_USE_CASE = 'EMAIL_TEMPLATE_ATTACHMENT'
const PDF_ACCEPT = 'application/pdf'

function toEnvelope(result: UploadedFile): AttachmentFileEnvelope {
  return {
    display: result.file_name,
    value: {
      uuid: result.uuid,
      file_name: result.file_name,
      file_url: result.file_url ?? null,
      file_size: result.file_size ?? null,
      mime_type: result.mime_type ?? PDF_ACCEPT,
    },
  }
}

function formatSize(bytes: number | null): string {
  if (!bytes || bytes <= 0) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

interface TemplateAttachmentsEditorProps {
  attachments: TemplateAttachment[]
  onChange: (next: TemplateAttachment[]) => void
  disabled?: boolean
  /** Letters: the PDFs travel after the letter rather than replacing it. */
  enclosures?: boolean
}

export function TemplateAttachmentsEditor({
  attachments,
  onChange,
  disabled = false,
  enclosures = false,
}: TemplateAttachmentsEditorProps) {
  const { t } = useTranslation()
  const upload = useUploadFile()
  const addInputRef = useRef<HTMLInputElement>(null)
  const replaceInputRef = useRef<HTMLInputElement>(null)
  // Index of the row a pending "Replace" file dialog will overwrite.
  const [replaceIndex, setReplaceIndex] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)

  const uploadPdf = async (file: File): Promise<UploadedFile | null> => {
    try {
      return await upload.mutateAsync({ file, use_case: ATTACHMENT_USE_CASE })
    } catch (err) {
      toastApiError(
        err,
        t('emailTemplates.attachments.uploadError', "Couldn't upload the PDF."),
      )
      return null
    }
  }

  const handleAdd = async (files: FileList | null) => {
    if (!files || files.length === 0) return
    setBusy(true)
    const added: TemplateAttachment[] = []
    for (const file of Array.from(files)) {
      const result = await uploadPdf(file)
      if (!result) continue
      added.push({
        uuid: '',
        display_name: result.file_name,
        ordering: attachments.length + added.length,
        file: toEnvelope(result),
      })
    }
    if (added.length) onChange([...attachments, ...added])
    setBusy(false)
  }

  const handleReplace = async (files: FileList | null) => {
    const index = replaceIndex
    setReplaceIndex(null)
    if (index === null || !files || files.length === 0) return
    setBusy(true)
    const result = await uploadPdf(files[0])
    if (result) {
      const next = attachments.map((att, i) =>
        i === index ? { ...att, file: toEnvelope(result) } : att,
      )
      onChange(next)
    }
    setBusy(false)
  }

  const rename = (index: number, name: string) => {
    onChange(
      attachments.map((att, i) =>
        i === index ? { ...att, display_name: name } : att,
      ),
    )
  }

  const remove = (index: number) => {
    // Destructive action - confirm before removing (repo style guide).
    if (
      !window.confirm(
        t(
          'emailTemplates.attachments.confirmRemove',
          'Are you sure you want to remove this attachment?',
        ),
      )
    ) {
      return
    }
    onChange(
      attachments
        .filter((_, i) => i !== index)
        .map((att, i) => ({ ...att, ordering: i })),
    )
  }

  return (
    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50/60 p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div>
          <p className="text-sm font-medium text-slate-900">
            {enclosures
              ? t('emailTemplates.attachments.enclosuresTitle', 'Enclosures')
              : t('emailTemplates.attachments.title', 'PDF attachments')}
          </p>
          <p className="text-xs text-slate-500">
            {enclosures
              ? t(
                  'emailTemplates.attachments.enclosuresDescription',
                  'Uploaded PDFs go out after the letter on every send, for example a technical bulletin.',
                )
              : t(
                  'emailTemplates.attachments.description',
                  'Uploaded PDFs go with every email this template sends, after the PDF of the message when PDF attachment is ticked.',
                )}
          </p>
        </div>
        {!disabled && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid="attachment-add-btn"
            disabled={busy}
            onClick={() => addInputRef.current?.click()}
          >
            {busy ? (
              <Loader2 className="animate-spin" size={14} />
            ) : (
              <Upload size={14} />
            )}
            {t('emailTemplates.attachments.add', 'Add PDF')}
          </Button>
        )}
      </div>

      {disabled ? (
        // Reached only while editing a legacy organisation override (subject /
        // body only). Attachments live on the tenant's own full copy.
        <p className="py-3 text-center text-xs text-slate-500">
          {t(
            'emailTemplates.attachments.unavailable',
            "PDF attachments aren't available while editing an organisation override.",
          )}
        </p>
      ) : attachments.length === 0 && !busy ? (
        <p className="py-3 text-center text-xs text-slate-400">
          {t('emailTemplates.attachments.empty', 'No PDFs attached yet.')}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {attachments.map((att, index) => {
            const viewUrl = att.file.value.file_url
            return (
              <li
                key={att.uuid || `new-${index}-${att.file.value.uuid}`}
                data-testid="attachment-row"
                className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-2.5 py-2"
              >
                <FileText
                  className="shrink-0 text-slate-400"
                  size={18}
                  aria-hidden
                />
                <div className="min-w-0 flex-1">
                  <Input
                    value={att.display_name}
                    disabled={disabled || busy}
                    placeholder={
                      att.file.value.file_name ||
                      t(
                        'emailTemplates.attachments.namePlaceholder',
                        'Attachment name',
                      )
                    }
                    onChange={(e) => rename(index, e.target.value)}
                    className="h-8 text-sm"
                  />
                  <p className="mt-0.5 truncate text-xs text-slate-400">
                    {att.file.value.file_name}
                    {formatSize(att.file.value.file_size)
                      ? ` · ${formatSize(att.file.value.file_size)}`
                      : ''}
                  </p>
                </div>
                {viewUrl && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={busy}
                    onClick={() =>
                      window.open(viewUrl, '_blank', 'noopener,noreferrer')
                    }
                    title={t(
                      'emailTemplates.attachments.viewTitle',
                      'Open this PDF in a new tab',
                    )}
                  >
                    <ExternalLink size={14} />
                    {t('emailTemplates.attachments.view', 'View')}
                  </Button>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={disabled || busy}
                  onClick={() => {
                    setReplaceIndex(index)
                    replaceInputRef.current?.click()
                  }}
                  title={t(
                    'emailTemplates.attachments.replaceTitle',
                    'Replace this PDF',
                  )}
                >
                  <RefreshCw size={14} />
                  {t('emailTemplates.attachments.replace', 'Replace')}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  disabled={disabled || busy}
                  onClick={() => remove(index)}
                  title={t(
                    'emailTemplates.attachments.removeTitle',
                    'Remove this PDF',
                  )}
                >
                  <Trash2 size={14} />
                </Button>
              </li>
            )
          })}
          {busy && (
            <li
              data-testid="attachment-uploading"
              className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-2.5 py-3 text-sm text-slate-500"
            >
              <Loader2
                className="shrink-0 animate-spin text-slate-400"
                size={18}
                aria-hidden
              />
              {t('emailTemplates.attachments.uploading', 'Uploading PDF…')}
            </li>
          )}
        </ul>
      )}

      {/* Hidden inputs are the sanctioned exception to the raw-input ban. */}
      <input
        ref={addInputRef}
        type="file"
        accept={PDF_ACCEPT}
        multiple
        data-testid="attachment-add-input"
        className="hidden"
        onChange={(e) => {
          void handleAdd(e.target.files)
          e.target.value = ''
        }}
      />
      <input
        ref={replaceInputRef}
        type="file"
        accept={PDF_ACCEPT}
        className="hidden"
        onChange={(e) => {
          void handleReplace(e.target.files)
          e.target.value = ''
        }}
      />
    </div>
  )
}
